import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Modal, Field } from "./Rooms";
import { formatCurrency } from "@/lib/format";
import {
    Plus, Coffee, UtensilsCrossed, Sparkles, Ban, ShieldCheck,
    Pencil, Trash2, Percent, Calendar as CalIcon, Link2, CalendarDays, X,
} from "lucide-react";
import { toast } from "sonner";

const BOARD_META = {
    RO: { label: "Vetëm dhoma",       icon: Ban,             color: "bg-muted text-foreground dark:bg-slate-500/15 dark:text-slate-300" },
    BB: { label: "Fjetje + Mëngjes",  icon: Coffee,          color: "bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300" },
    HB: { label: "Gjysmë pansion",    icon: UtensilsCrossed, color: "bg-orange-50 text-orange-800 dark:bg-orange-500/10 dark:text-orange-300" },
    FB: { label: "Pansion i plotë",   icon: UtensilsCrossed, color: "bg-red-50 text-red-800 dark:bg-red-500/10 dark:text-red-300" },
    AI: { label: "All Inclusive",     icon: Sparkles,        color: "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300" },
};

const POLICY_LABEL = {
    flexible: "Fleksibël",
    moderate: "Mesatare",
    strict: "Strikte",
    non_refundable: "Pa rimbursim",
    custom: "E personalizuar",
};

const DEFAULT_FORM = {
    code: "",
    name: "",
    description: "",
    base_price: 80,
    currency: "EUR",
    board_type: "BB",
    include_breakfast: true,
    refundable: true,
    cancellation_policy: "flexible",
    cancellation_notes: "",
    min_stay: 1,
    max_stay: 30,
    min_advance_booking: 0,
    max_advance_booking: 365,
    commission_pct: 15,
    room_type_ids: [],
    active: true,
};

export default function RatePlans() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // rate plan being edited, or null
    const [creating, setCreating] = useState(false);
    const [seasoning, setSeasoning] = useState(null); // rate plan for which we're editing seasons

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/rate-plans");
            setItems(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ngarkimi i planeve të tarifave dështoi");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const remove = async (rp) => {
        if (!window.confirm(`Të fshihet plani i tarifës "${rp.name}" (${rp.code})?`)) return;
        try {
            await api.delete(`/rate-plans/${rp.id}`);
            toast.success("Plani i tarifës u fshi");
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const activeCount = useMemo(() => items.filter((r) => r.active).length, [items]);
    const mappedCount = useMemo(() => items.filter((r) => r.channex_rate_plan_ids?.length > 0).length, [items]);

    return (
        <>
            <Topbar
                title="Planet e Tarifave"
                subtitle={`${activeCount} aktive · ${mappedCount} të lidhura me OTA`}
                actions={
                    <button
                        onClick={() => setCreating(true)}
                        className="akm-btn-primary"
                        data-testid="new-rate-plan-btn"
                    >
                        <Plus size={14} /> Plan i ri tarife
                    </button>
                }
            />
            <div className="flex-1 overflow-auto p-6 space-y-4">
                <div className="rounded-xl border border-border bg-card p-4">
                    <div className="text-[13px] font-semibold tracking-tight">Çfarë janë planet e tarifave?</div>
                    <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                        Planet e tarifave janë paketa çmimesh + politikash (lloji i pansionit, anulimi, qëndrimi min/maks, komisioni).
                        Bashkangjit një plan tarife me rezervimin që të fiksojë çmimin, planin e vakteve dhe rimbursueshmërinë.
                        Pas mapimit nga admini i Shpërndarjes, rezervimet nga OTA-t (Booking.com, Airbnb, Expedia, Agoda) do të
                        lidhen automatikisht me planin përkatës të tarifës.
                    </p>
                </div>

                {loading ? (
                    <div className="p-10 text-center text-sm text-muted-foreground">Duke u ngarkuar…</div>
                ) : items.length === 0 ? (
                    <div className="akm-card p-10 text-center">
                        <div className="text-[14px] font-semibold">Ende s&apos;ka plane tarifash</div>
                        <p className="mt-1 text-[12px] text-muted-foreground">
                            Fillo me standardet e industrisë: <b>BAR</b> (Tarifa më e mirë e disponueshme),
                            <b> NRF</b> (Pa rimbursim), <b>HB</b> (Gjysmë pansion).
                        </p>
                        <button onClick={() => setCreating(true)}
                                className="akm-btn-primary mt-4 mx-auto"
                                data-testid="empty-new-rate-plan-btn">
                            <Plus size={14} /> Krijo planin e parë të tarifës
                        </button>
                    </div>
                ) : (
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {items.map((rp) => (
                            <RatePlanCard
                                key={rp.id}
                                plan={rp}
                                onEdit={() => setEditing(rp)}
                                onDelete={() => remove(rp)}
                                onSeasons={() => setSeasoning(rp)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {creating && (
                <RatePlanForm
                    onClose={() => setCreating(false)}
                    onSaved={async () => { setCreating(false); await load(); }}
                />
            )}
            {editing && (
                <RatePlanForm
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await load(); }}
                />
            )}
            {seasoning && (
                <SeasonalityModal
                    plan={seasoning}
                    onClose={() => setSeasoning(null)}
                />
            )}
        </>
    );
}

function RatePlanCard({ plan, onEdit, onDelete, onSeasons }) {
    const board = BOARD_META[plan.board_type] || BOARD_META.RO;
    const Icon = board.icon;
    const mapped = plan.channex_rate_plan_ids?.length > 0;
    return (
        <div className="akm-card p-4 flex flex-col gap-3" data-testid={`rate-plan-card-${plan.code}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <code className="rounded bg-orange-50 px-1.5 py-0.5 text-[10px] font-bold text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">
                            {plan.code}
                        </code>
                        {!plan.active && (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground dark:bg-slate-500/20 dark:text-slate-300">
                                Joaktiv
                            </span>
                        )}
                    </div>
                    <div className="mt-1 text-[14px] font-semibold tracking-tight truncate">{plan.name}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                    <button onClick={onSeasons}
                            className="rounded-md p-1.5 text-muted-foreground hover:bg-orange-50 hover:text-orange-600 dark:hover:bg-orange-500/10"
                            data-testid={`seasons-rate-plan-${plan.code}`}
                            title="Sezonaliteti (mbivendosje datash)">
                        <CalendarDays size={13} />
                    </button>
                    <button onClick={onEdit}
                            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                            data-testid={`edit-rate-plan-${plan.code}`}
                            title="Modifiko">
                        <Pencil size={13} />
                    </button>
                    <button onClick={onDelete}
                            className="rounded-md p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
                            data-testid={`delete-rate-plan-${plan.code}`}
                            title="Fshi">
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${board.color}`}>
                    <Icon size={10} /> {board.label}
                </span>
                {plan.refundable ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                        <ShieldCheck size={10} /> Rimbursueshëm
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-300">
                        <Ban size={10} /> Pa rimbursim
                    </span>
                )}
                <span className="inline-flex items-center gap-1 rounded-full bg-muted/40 px-2 py-0.5 font-medium text-muted-foreground dark:bg-slate-500/15 dark:text-slate-300">
                    {POLICY_LABEL[plan.cancellation_policy] || plan.cancellation_policy}
                </span>
            </div>

            {plan.description && (
                <p className="text-[12px] leading-relaxed text-muted-foreground line-clamp-2">{plan.description}</p>
            )}

            <div className="grid grid-cols-3 gap-2 text-[11px]">
                <Stat label="Çmimi bazë">
                    <b className="text-[13px]">{formatCurrency(plan.base_price || 0, plan.currency || "EUR")}</b>
                </Stat>
                <Stat label="Qëndrimi">
                    <span><b>{plan.min_stay}</b>–<b>{plan.max_stay}</b> netë</span>
                </Stat>
                <Stat label="Komisioni">
                    <span className="inline-flex items-center gap-0.5"><b>{plan.commission_pct}</b><Percent size={9} /></span>
                </Stat>
            </div>

            <div className="mt-auto flex items-center justify-between border-t border-border pt-2">
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    <CalIcon size={10} />
                    Rezervim {plan.min_advance_booking}–{plan.max_advance_booking} ditë përpara
                </div>
                <div className={`flex items-center gap-1 text-[10px] font-semibold ${
                    mapped ? "text-emerald-700 dark:text-emerald-300" : "text-muted-foreground"
                }`}>
                    <Link2 size={10} />
                    {mapped ? `Të lidhura × ${plan.channex_rate_plan_ids.length}` : "Pa lidhje me OTA"}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, children }) {
    return (
        <div className="rounded border border-border bg-muted/30 p-2">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="mt-0.5 text-[12px]">{children}</div>
        </div>
    );
}

function RatePlanForm({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial ? { ...DEFAULT_FORM, ...initial } : DEFAULT_FORM);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const body = {
                code: form.code.trim().toUpperCase(),
                name: form.name.trim(),
                description: form.description || null,
                base_price: Number(form.base_price) || 0,
                currency: form.currency || "EUR",
                board_type: form.board_type,
                include_breakfast: !!form.include_breakfast,
                refundable: !!form.refundable,
                cancellation_policy: form.cancellation_policy,
                cancellation_notes: form.cancellation_notes || null,
                min_stay: Number(form.min_stay) || 1,
                max_stay: Number(form.max_stay) || 30,
                min_advance_booking: Number(form.min_advance_booking) || 0,
                max_advance_booking: Number(form.max_advance_booking) || 365,
                commission_pct: Number(form.commission_pct) || 0,
                active: !!form.active,
            };
            if (isEdit) {
                await api.patch(`/rate-plans/${initial.id}`, body);
                toast.success("Plani i tarifës u përditësua");
            } else {
                await api.post("/rate-plans", body);
                toast.success("Plani i tarifës u krijua");
            }
            onSaved();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={isEdit ? `Modifiko planin · ${initial.code}` : "Plan i ri tarife"}
            onClose={onClose}
            width="max-w-2xl"
        >
            <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-3 gap-2">
                    <Field label="Kodi (unik)">
                        <input className="akm-input font-mono uppercase" required maxLength={16}
                               value={form.code}
                               disabled={isEdit}
                               onChange={(e) => set({ code: e.target.value.toUpperCase() })}
                               data-testid="rate-plan-code" />
                    </Field>
                    <div className="col-span-2">
                        <Field label="Emri i shfaqur">
                            <input className="akm-input" required
                                   value={form.name}
                                   onChange={(e) => set({ name: e.target.value })}
                                   data-testid="rate-plan-name" />
                        </Field>
                    </div>
                </div>

                <Field label="Përshkrimi (shfaqet në fatura dhe motorin e rezervimeve)">
                    <textarea className="akm-input min-h-[60px]"
                              value={form.description || ""}
                              onChange={(e) => set({ description: e.target.value })}
                              data-testid="rate-plan-description" />
                </Field>

                <div className="grid grid-cols-3 gap-2">
                    <Field label="Çmimi bazë">
                        <input className="akm-input" type="number" step="0.01" min="0"
                               value={form.base_price}
                               onChange={(e) => set({ base_price: e.target.value })}
                               data-testid="rate-plan-price" />
                    </Field>
                    <Field label="Monedha">
                        <select className="akm-input" value={form.currency}
                                onChange={(e) => set({ currency: e.target.value })}>
                            <option>EUR</option><option>USD</option><option>ALL</option><option>GBP</option>
                        </select>
                    </Field>
                    <Field label="Komisioni %">
                        <input className="akm-input" type="number" step="0.1" min="0" max="100"
                               value={form.commission_pct}
                               onChange={(e) => set({ commission_pct: e.target.value })}
                               data-testid="rate-plan-commission" />
                    </Field>
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <Field label="Lloji i pansionit">
                        <select className="akm-input" value={form.board_type}
                                onChange={(e) => set({ board_type: e.target.value, include_breakfast: e.target.value !== "RO" })}
                                data-testid="rate-plan-board">
                            <option value="RO">Vetëm dhoma</option>
                            <option value="BB">Fjetje + Mëngjes</option>
                            <option value="HB">Gjysmë pansion</option>
                            <option value="FB">Pansion i plotë</option>
                            <option value="AI">All Inclusive</option>
                        </select>
                    </Field>
                    <Field label="Politika e anulimit">
                        <select className="akm-input" value={form.cancellation_policy}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    set({ cancellation_policy: v, refundable: v !== "non_refundable" });
                                }}
                                data-testid="rate-plan-policy">
                            <option value="flexible">Fleksibël (24 orë)</option>
                            <option value="moderate">Mesatare (7 ditë)</option>
                            <option value="strict">Strikte (30 ditë)</option>
                            <option value="non_refundable">Pa rimbursim</option>
                            <option value="custom">E personalizuar</option>
                        </select>
                    </Field>
                </div>

                {form.cancellation_policy === "custom" && (
                    <Field label="Shënime për anulimin">
                        <textarea className="akm-input"
                                  value={form.cancellation_notes || ""}
                                  onChange={(e) => set({ cancellation_notes: e.target.value })} />
                    </Field>
                )}

                <div className="grid grid-cols-4 gap-2">
                    <Field label="Qëndrimi min">
                        <input className="akm-input" type="number" min="1"
                               value={form.min_stay}
                               onChange={(e) => set({ min_stay: e.target.value })} />
                    </Field>
                    <Field label="Qëndrimi maks">
                        <input className="akm-input" type="number" min="1"
                               value={form.max_stay}
                               onChange={(e) => set({ max_stay: e.target.value })} />
                    </Field>
                    <Field label="Njoftim min (ditë)">
                        <input className="akm-input" type="number" min="0"
                               value={form.min_advance_booking}
                               onChange={(e) => set({ min_advance_booking: e.target.value })} />
                    </Field>
                    <Field label="Njoftim maks (ditë)">
                        <input className="akm-input" type="number" min="0"
                               value={form.max_advance_booking}
                               onChange={(e) => set({ max_advance_booking: e.target.value })} />
                    </Field>
                </div>

                <div className="flex flex-wrap items-center gap-4 rounded-md border border-border bg-muted/30 p-3 text-[12px]">
                    <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={form.include_breakfast}
                               onChange={(e) => set({ include_breakfast: e.target.checked })} />
                        Përfshi mëngjesin
                    </label>
                    <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={form.refundable}
                               onChange={(e) => set({ refundable: e.target.checked })} />
                        Rimbursueshëm
                    </label>
                    <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={form.active}
                               onChange={(e) => set({ active: e.target.checked })} />
                        Aktiv
                    </label>
                </div>

                <div className="flex items-center justify-between gap-2 pt-2">
                    {isEdit && initial.channex_rate_plan_ids?.length > 0 && (
                        <div className="text-[11px] text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1">
                            <Link2 size={11} /> Aktualisht i lidhur me {initial.channex_rate_plan_ids.length} plan(e) tarife OTA
                        </div>
                    )}
                    <div className="ml-auto flex gap-2">
                        <button type="button" onClick={onClose} className="akm-btn-ghost">Anulo</button>
                        <button type="submit" disabled={saving} className="akm-btn-primary"
                                data-testid="save-rate-plan">
                            {saving ? "Duke ruajtur…" : isEdit ? "Ruaj ndryshimet" : "Krijo planin"}
                        </button>
                    </div>
                </div>
            </form>
        </Modal>
    );
}


// -------------------------------------------------------------------
// Seasonality modal — manage date-range overrides + weekend rule
// -------------------------------------------------------------------
function SeasonalityModal({ plan, onClose }) {
    const [overrides, setOverrides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [preview, setPreview] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/rate-plans/${plan.id}/overrides`);
            setOverrides(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [plan.id]);

    const remove = async (ov) => {
        if (!window.confirm(`Të fshihet mbivendosja "${ov.label}"?`)) return;
        try {
            await api.delete(`/rate-plans/${plan.id}/overrides/${ov.id}`);
            toast.success("Mbivendosja u fshi");
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const checkPrice = async (dateStr) => {
        try {
            const { data } = await api.get(`/rate-plans/${plan.id}/price?date_str=${dateStr}`);
            setPreview(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <Modal title={`Sezonaliteti · ${plan.code} — ${plan.name}`} onClose={onClose} width="max-w-3xl">
            <div className="space-y-4">
                <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-[12px] leading-relaxed text-orange-900 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-200">
                    <b>Çmimi bazë:</b> {formatCurrency(plan.base_price || 0, plan.currency || "EUR")} · <b>Qëndrimi min:</b> {plan.min_stay} netë.
                    Mbivendosjet aplikohen mbi tarifën bazë. <b>Prioriteti</b> më i lartë fiton në rast mbivendosjesh. Mbivendosjet vetëm-fundjavë
                    aplikohen të Premteve/Shtunave/Dielave.
                </div>

                {/* Preview */}
                <div className="rounded-md border border-border bg-muted/30 p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                        Parashiko çmimin efektiv
                    </div>
                    <div className="flex items-center gap-2">
                        <input type="date" className="akm-input text-[12px]"
                               onChange={(e) => e.target.value && checkPrice(e.target.value)}
                               data-testid="seasonality-preview-date" />
                        {preview && (
                            <div className="text-[12px]">
                                Më <b>{preview.date}</b> → <b className="text-orange-600">{preview.currency} {preview.price}</b>
                                {preview.override && <span className="ml-2 text-muted-foreground">({preview.override.label})</span>}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <div className="text-[13px] font-semibold">Mbivendosjet ({overrides.length})</div>
                    <button onClick={() => setAdding(true)} className="akm-btn-primary text-[11px]"
                            data-testid="add-override-btn">
                        <Plus size={12} /> Shto mbivendosje
                    </button>
                </div>

                {adding && (
                    <OverrideForm
                        planId={plan.id}
                        onClose={() => setAdding(false)}
                        onSaved={async () => { setAdding(false); await load(); }}
                    />
                )}

                {loading ? (
                    <div className="p-4 text-center text-[12px] text-muted-foreground">Duke u ngarkuar…</div>
                ) : overrides.length === 0 ? (
                    <div className="rounded border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">
                        Ende s&apos;ka mbivendosje. Shto një bllok për Verën, periudhë festash, ose shtesë fundjave.
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        {overrides.map((ov) => (
                            <div key={ov.id}
                                 data-testid={`override-row-${ov.id}`}
                                 className="flex items-center gap-2 rounded-md border border-border bg-card p-2 text-[12px]">
                                <span className="inline-flex h-6 w-8 items-center justify-center rounded bg-orange-50 text-[10px] font-bold text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">
                                    P{ov.priority}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate font-semibold">{ov.label}</div>
                                    <div className="text-[10px] text-muted-foreground">
                                        {ov.start_date} → {ov.end_date}
                                        {ov.weekend_only && <span className="ml-1 rounded bg-amber-50 px-1 py-0.5 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">vetëm fundjavë</span>}
                                    </div>
                                </div>
                                <div className="text-right">
                                    {ov.override_price != null && (
                                        <div className="font-semibold">{plan.currency} {ov.override_price}</div>
                                    )}
                                    {ov.override_min_stay && (
                                        <div className="text-[10px] text-muted-foreground">min {ov.override_min_stay}n</div>
                                    )}
                                </div>
                                <button onClick={() => remove(ov)}
                                        className="rounded-md p-1 text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
                                        title="Fshi">
                                    <X size={13} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
}


function OverrideForm({ planId, onClose, onSaved }) {
    const [label, setLabel] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [price, setPrice] = useState("");
    const [minStay, setMinStay] = useState("");
    const [weekendOnly, setWeekendOnly] = useState(false);
    const [priority, setPriority] = useState(10);
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post(`/rate-plans/${planId}/overrides`, {
                rate_plan_id: planId,
                label: label.trim(),
                start_date: startDate,
                end_date: endDate,
                override_price: price === "" ? null : Number(price),
                override_min_stay: minStay === "" ? null : Number(minStay),
                weekend_only: weekendOnly,
                priority: Number(priority) || 0,
            });
            toast.success("Mbivendosja u shtua");
            onSaved();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="rounded-md border border-orange-300 bg-orange-50/40 p-3 space-y-2 dark:border-orange-500/40 dark:bg-orange-500/5">
            <div className="grid grid-cols-2 gap-2">
                <Field label="Etiketa">
                    <input required className="akm-input" value={label}
                           onChange={(e) => setLabel(e.target.value)}
                           placeholder="p.sh. Vera 2026"
                           data-testid="override-label" />
                </Field>
                <Field label="Prioriteti">
                    <input type="number" min="0" max="100" className="akm-input"
                           value={priority} onChange={(e) => setPriority(e.target.value)}
                           title="Prioriteti më i lartë fiton në rast mbivendosjesh" />
                </Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <Field label="Data e fillimit">
                    <input type="date" required className="akm-input"
                           value={startDate} onChange={(e) => setStartDate(e.target.value)}
                           data-testid="override-start" />
                </Field>
                <Field label="Data e mbarimit">
                    <input type="date" required className="akm-input"
                           value={endDate} onChange={(e) => setEndDate(e.target.value)}
                           data-testid="override-end" />
                </Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <Field label="Çmimi mbivendosës (bosh = ruaj bazën)">
                    <input type="number" step="0.01" min="0" className="akm-input"
                           value={price} onChange={(e) => setPrice(e.target.value)}
                           data-testid="override-price" />
                </Field>
                <Field label="Qëndrimi min mbivendosës">
                    <input type="number" min="1" className="akm-input"
                           value={minStay} onChange={(e) => setMinStay(e.target.value)} />
                </Field>
            </div>
            <label className="inline-flex items-center gap-2 text-[12px]">
                <input type="checkbox" checked={weekendOnly}
                       onChange={(e) => setWeekendOnly(e.target.checked)}
                       data-testid="override-weekend" />
                Vetëm fundjavë (Pre / Sht / Die)
            </label>
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onClose} className="akm-btn-ghost text-[11px]">Anulo</button>
                <button type="submit" disabled={saving} className="akm-btn-primary text-[11px]"
                        data-testid="override-save">
                    {saving ? "Duke ruajtur…" : "Shto mbivendosje"}
                </button>
            </div>
        </form>
    );
}
